import { useState, useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Logo, LogoMark } from "@/components/skl/Logo";
import { ContactForm } from "@/components/skl/ContactForm";
import { SpotlightCard } from "@/components/skl/SpotlightCard";
import { TypingText } from "@/components/skl/TypingText";
import { WireGlobe } from "@/components/skl/WireGlobe";
import { Marquee } from "@/components/skl/Marquee";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { supabase } from "@/integrations/supabase/client";
import {
  Wrench, Zap, Sparkles, Hammer, BrickWall, Building2, Check, ArrowRight, ArrowUp,
  ShieldCheck, Smartphone, FileText, Send, Rocket,
  Instagram, LogIn, LayoutDashboard,
} from "lucide-react";
import { ProfileMenu } from "@/components/skl/ProfileMenu";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "SKLflow — Professionelle hjemmesider til servicevirksomheder" },
      { name: "description", content: "Vi udvikler skræddersyede hjemmesider til danske servicevirksomheder. Modtag et uforpligtende udkast inden for 24 timer - betaling sker først, når du er tilfreds med resultatet." },
      { property: "og:title", content: "SKLflow — Professionelle hjemmesider til servicevirksomheder" },
      { property: "og:description", content: "Modtag et uforpligtende hjemmeside-udkast inden for 24 timer. Betaling sker først ved godkendelse." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sklflow.com/" },
    ],
    links: [{ rel: "canonical", href: "https://sklflow.com/" }],
  }),
  component: Home,
});

const trustSignals = [
  "Ingen binding",
  "Udkast leveret inden for 24 timer",
  "Uforpligtende udkast",
  "Specialiseret i servicevirksomheder",
];

const steps = [
  {
    n: "01",
    icon: Rocket,
    title: "Vi udarbejder dit udkast",
    body: "Inden for 24 timer modtager du et gennemarbejdet udkast til din nye hjemmeside - klar til gennemgang.",
  },
  {
    n: "02",
    icon: Check,
    title: "Du betaler kun ved godkendelse",
    body: "Er udkastet ikke det rette match, sender vi ingen faktura. Godkender du løsningen, kan vi udvide med yderligere ydelser til brand og hjemmeside på fair vilkår.",
  },
];

const industries = [
  { icon: Wrench, label: "VVS" },
  { icon: Zap, label: "El-installatør" },
  { icon: Sparkles, label: "Rengøring" },
  { icon: BrickWall, label: "Murer" },
  { icon: Hammer, label: "Tømrer" },
  { icon: Hammer, label: "Håndværker" },
  { icon: Building2, label: "Ejendomsmægler" },
];

const included = [
  { icon: Sparkles, title: "Professionelt design", body: "Skræddersyet til din branche og din målgruppe." },
  { icon: Smartphone, title: "Optimeret til alle enheder", body: "Hurtig indlæsning og et gennemført udtryk - på tværs af mobil, tablet og desktop." },
  { icon: FileText, title: "Færdigskrevet indhold", body: "Vi leverer tekst og struktur, så du kan gå live uden at skrive en linje selv." },
  { icon: Send, title: "Integreret kontaktformular", body: "Sikrer at potentielle kunder nemt kan komme i dialog med dig." },
  { icon: Rocket, title: "Klar til lancering", body: "Ved godkendelse kan hjemmesiden være online med det samme." },
];


function Home() {
  const howRef = useScrollReveal<HTMLElement>();
  const targetRef = useScrollReveal<HTMLElement>();
  const inclRef = useScrollReveal<HTMLElement>();
  const contactRef = useScrollReveal<HTMLElement>();

  const [showTopBtn, setShowTopBtn] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTopBtn(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });

    const checkRole = async (userId: string | undefined) => {
      if (!userId) { setIsAdmin(false); return; }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    };

    supabase.auth.getSession().then(({ data }) => {
      setSignedIn(!!data.session);
      checkRole(data.session?.user.id);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(!!session);
      checkRole(session?.user.id);
    });
    return () => {
      window.removeEventListener("scroll", onScroll);
      sub.subscription.unsubscribe();
    };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleSignedOut = () => {
    setIsAdmin(false);
    setSignedIn(false);
  };




  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      {/* Global noise overlay */}
      <div aria-hidden className="noise-overlay pointer-events-none fixed inset-0 z-50" />

      {/* Glass nav */}
      <header className="sticky top-0 z-40 w-full">
        <div className="backdrop-blur-xl bg-card/80 border-b border-foreground/10 px-4 sm:px-8 py-3 flex items-center justify-between shadow-[0_8px_32px_-12px_rgba(0,0,0,0.6)]">
          <Logo />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-muted-foreground">
            <a href="#hvordan" className="hover:text-foreground transition">Proces</a>
            <a href="#maalgruppe" className="hover:text-foreground transition">Målgruppe</a>
            <a href="#inkluderet" className="hover:text-foreground transition">Ydelser</a>
            <a href="#kontakt" className="hover:text-foreground transition">Kontakt</a>
          </nav>
          <div className="flex items-center gap-2">
            {signedIn ? (
              <>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="inline-flex items-center gap-1.5 rounded-full border border-foreground/15 px-3 py-2 text-sm font-semibold hover:bg-card/60 transition"
                  >
                    <LayoutDashboard className="h-4 w-4" /> Admin
                  </Link>
                )}
                <ProfileMenu onSignedOut={handleSignedOut} />


              </>
            ) : (
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 rounded-full border border-foreground/15 px-3 py-2 text-sm font-semibold hover:bg-card/60 transition"
              >
                <LogIn className="h-4 w-4" /> Log ind
              </Link>
            )}
            <a
              href="#kontakt"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition shadow-[0_0_24px_-4px_color-mix(in_oklab,var(--primary)_60%,transparent)]"
            >
              Bestil udkast <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      <main>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col items-center justify-center px-6 pt-20 pb-24 text-center overflow-hidden">

        {/* Aurora orbs */}
        <div aria-hidden className="absolute top-[-10%] left-[-10%] w-[55%] h-[55%] rounded-full bg-primary/20 blur-[120px] animate-pulse-soft" />
        <div aria-hidden className="absolute bottom-[-10%] right-[-10%] w-[45%] h-[45%] rounded-full bg-highlight/15 blur-[120px] animate-pulse-soft" style={{ animationDelay: "2s" }} />
        <div aria-hidden className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[60%] h-[40%] rounded-full bg-accent/10 blur-[140px]" />


        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-accent text-xs font-semibold mb-8 backdrop-blur-md">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
            </span>
            Uforpligtende udkast • Betaling først ved godkendelse
          </div>

          <h1 className="font-display text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-snug mb-8">
            <TypingText
              text={[
                { text: "Hjemmesider der styrker", className: "block text-foreground mb-2" },
                { text: "din virksomheds troværdighed", className: "block text-transparent bg-clip-text bg-gradient-to-r from-accent via-primary to-highlight animate-gradient-x" },
              ]}
            />
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Vi udvikler skræddersyede hjemmesider til danske servicevirksomheder.<br className="hidden md:block" />
            Modtag et uforpligtende udkast - betaling sker først, når du er tilfreds.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#kontakt"
              className="group relative inline-flex items-center gap-2 px-8 py-4 bg-primary rounded-full font-bold text-primary-foreground overflow-hidden transition-all hover:scale-105 active:scale-95 shadow-[0_0_40px_-8px_color-mix(in_oklab,var(--primary)_70%,transparent)]"
            >
              <span className="relative z-10">Bestil dit udkast</span>
              <ArrowRight className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-1" />
              <div className="absolute inset-0 bg-gradient-to-r from-accent to-highlight opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <a
              href="#hvordan"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold border border-foreground/15 hover:border-foreground/30 hover:bg-card/40 backdrop-blur-md transition-all"
            >
              Se vores proces
            </a>
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="py-10 border-y border-foreground/5 bg-card/30 backdrop-blur-sm">
        <Marquee>
          {trustSignals.concat(trustSignals).map((t, i) => (
            <span key={i} className="flex items-center gap-3 text-sm font-medium uppercase tracking-widest text-muted-foreground whitespace-nowrap">
              <ShieldCheck className="h-4 w-4 text-accent shrink-0" />
              {t}
              <span className="text-primary/40 ml-12">◆</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* HOW IT WORKS */}
      <section ref={howRef} id="hvordan" className="relative py-28 md:py-36 px-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-16 max-w-3xl">
            <p className="text-accent font-bold tracking-[0.2em] uppercase text-xs mb-4">Vores proces</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
              Fra brief til{" "}
              <span className="italic font-light text-muted-foreground">færdig</span>
              <br />
              hjemmeside i to trin
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {steps.map((s, i) => (
              <div
                key={s.n}
                style={{ transitionDelay: `${i * 150}ms` }}
                className="reveal group relative p-8 md:p-10 rounded-3xl bg-card/40 border border-foreground/10 hover:border-primary/40 backdrop-blur-xl transition-all duration-500 overflow-hidden"
              >
                <div className="absolute -right-4 -top-8 font-display text-[10rem] font-black text-foreground/[0.04] select-none leading-none group-hover:text-primary/10 transition-colors">
                  {s.n}
                </div>
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-primary/15 border border-primary/30 text-accent flex items-center justify-center mb-6">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-2xl font-bold mb-3">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR WHOM */}
      <section ref={targetRef} id="maalgruppe" className="relative py-28 md:py-36 px-6 overflow-hidden">
        <div aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[60%] bg-primary/10 blur-[140px] rounded-full" />
        <div className="relative mx-auto max-w-5xl text-center">
          <p className="text-accent font-bold tracking-[0.2em] uppercase text-xs mb-4">Målgruppe</p>
          <h2 className="font-display text-3xl sm:text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Specialiseret i
            <br className="sm:hidden" />{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-primary animate-gradient-x">
              servicevirksomheder
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-14">
            Vi samarbejder primært med virksomheder inden for følgende brancher:
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {industries.map(({ icon: Icon, label }, i) => (
              <div key={label} style={{ transitionDelay: `${i * 60}ms` }} className="reveal">
                <SpotlightCard className="h-full rounded-2xl bg-card/40 border border-foreground/10 hover:border-accent/40 backdrop-blur-xl p-5 flex flex-col items-center text-center gap-3 transition-all hover:-translate-y-1">
                  <div className="h-11 w-11 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center text-accent">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-semibold">{label}</span>
                </SpotlightCard>
              </div>
            ))}
          </div>

          <div className="mt-14 inline-block p-5 px-7 rounded-2xl bg-gradient-to-r from-primary/15 to-transparent border-l-2 border-primary text-accent italic">
            Leverer din virksomhed en service, er vi den rette samarbejdspartner.
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section ref={inclRef} id="inkluderet" className="relative py-28 md:py-36 px-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-16 max-w-3xl">
            <p className="text-accent font-bold tracking-[0.2em] uppercase text-xs mb-4">Inkluderet</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
              Alt er inkluderet -&nbsp;<span className="italic font-light text-muted-foreground">uden krav om tekniske forudsætninger</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {included.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                style={{ transitionDelay: `${i * 90}ms` }}
                className="reveal group rounded-3xl bg-card/40 border border-foreground/10 hover:border-primary/40 backdrop-blur-xl p-7 transition-all duration-500"
              >
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary to-highlight flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/30">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display mt-5 text-xl font-bold">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CONTACT */}
      <section ref={contactRef} id="kontakt" className="relative py-28 md:py-36 px-6 overflow-hidden">
        <div aria-hidden className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/15 blur-[140px] rounded-full pointer-events-none" />
        <div aria-hidden className="absolute bottom-0 right-10 w-72 h-72 bg-highlight/20 blur-[120px] rounded-full" />

        <div className="relative mx-auto max-w-3xl">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-accent font-bold tracking-[0.2em] uppercase text-xs mb-4">Kontakt</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-4">
              Anmod om et{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-primary to-highlight animate-gradient-x">
                uforpligtende udkast
              </span>
            </h2>
            <p className="text-muted-foreground">
              Udfyld formularen - det tager under to minutter. Vi vender tilbage inden for 24 timer.
            </p>
          </div>

          <div className="border-beam rounded-3xl bg-card/50 border border-foreground/10 backdrop-blur-2xl p-6 md:p-10 shadow-2xl">
            <ContactForm />
          </div>

          <div className="mt-8 flex items-center justify-center gap-3 rounded-2xl border border-foreground/10 bg-card/40 backdrop-blur-xl px-5 py-4 text-center text-sm">
            <Instagram className="h-5 w-5 text-accent shrink-0" />
            <span className="text-muted-foreground">
              Foretrækker du en hurtigere dialog? Kontakt os direkte på Instagram{" "}
              <a
                href="https://www.instagram.com/skl.flow/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-accent hover:underline"
              >
                @skl.flow
              </a>
            </span>
          </div>
        </div>
      </section>
      </main>

      {/* FOOTER */}
      <footer className="relative border-t border-foreground/10 bg-card/30 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-6 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <LogoMark className="h-8 w-8" />
              <span className="font-display font-bold text-lg tracking-tight">SKLflow</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Professionelle hjemmesider til servicevirksomheder
            </p>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/90">Links</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a href="#" className="text-muted-foreground hover:text-foreground transition">Forside</a></li>
              <li><a href="#hvordan" className="text-muted-foreground hover:text-foreground transition">Vores proces</a></li>
              <li><a href="#kontakt" className="text-muted-foreground hover:text-foreground transition">Kontakt</a></li>
            </ul>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/90">Juridisk</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/handelsbetingelser" className="text-muted-foreground hover:text-foreground transition">Handelsbetingelser</Link></li>
              <li><Link to="/privatlivspolitik" className="text-muted-foreground hover:text-foreground transition">Privatlivspolitik</Link></li>
              <li><Link to="/cookiepolitik" className="text-muted-foreground hover:text-foreground transition">Cookiepolitik</Link></li>
              <li><Link to="/returpolitik" className="text-muted-foreground hover:text-foreground transition">Returpolitik</Link></li>
            </ul>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/90">Virksomhed</h2>
            <p className="mt-3 text-sm text-muted-foreground">CVR: Ikke registreret virksomhed</p>
            <p className="mt-1 text-sm text-muted-foreground">© 2026 SKLflow</p>
          </div>
        </div>
      </footer>


      {/* Back to top */}
      <button
        onClick={scrollToTop}
        aria-label="Tilbage til toppen"
        className={`fixed bottom-6 right-6 z-40 rounded-full p-3 backdrop-blur-xl bg-card/60 border border-foreground/15 shadow-[0_8px_32px_-12px_rgba(0,0,0,0.6)] text-foreground transition-all duration-300 hover:scale-110 hover:bg-card/80 ${
          showTopBtn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </div>
  );
}
